import React, { useEffect, useRef } from 'react';
import { Dialog } from '@/shared/components/Dialog';
import { Textarea } from '@/shared/components/TextField';
import { CONTACT_REQUEST_LABELS as L } from '../../constants/labels';

const REASON_MAX_LENGTH = 500;

/** Props for the CancelContactRequestModal component. */
interface CancelContactRequestModalProps {
  open: boolean;
  /** Called when the user clicks "Wstecz" — returns to the status-update modal. */
  onBack: () => void;
  clientName: string | null | undefined;
  clientEmail: string | null | undefined;
  cancelNote: string;
  onCancelNoteChange: (value: string) => void;
  cancelNoteError: string;
  isSubmitting: boolean;
  submitError: string;
  onSubmit: () => void;
}

/**
 * Step-2 confirmation modal for cancelling a contact request.
 *
 * Displays read-only client details and a mandatory reason textarea. The user
 * must provide a reason before the API call is made; the reason is sent to the
 * client by email on success.
 *
 * While the API request is in-flight the entire form is disabled and both
 * action buttons show a reduced opacity to signal the pending state.
 */
const CancelContactRequestModal: React.FC<CancelContactRequestModalProps> = ({
  open,
  onBack,
  clientName,
  clientEmail,
  cancelNote,
  onCancelNoteChange,
  cancelNoteError,
  isSubmitting,
  submitError,
  onSubmit,
}) => {
  const reasonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cancelNoteError && reasonRef.current) {
      reasonRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [cancelNoteError]);

  const PLACEHOLDER = '—';

  return (
    <Dialog
      open={open}
      onClose={onBack}
      maxWidth={700}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      data-testid="cancel-cr-modal"
    >
      <div
        className="flex flex-col relative"
        style={{ maxHeight: 'calc(100vh - 48px)' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-2 shrink-0">
          <h2
            className="text-neutral-10 font-normal"
            style={{ fontSize: 22, lineHeight: '28px', fontWeight: 400 }}
            data-testid="cancel-cr-modal-title"
          >
            {L.CANCEL_CR_MODAL_TITLE}
          </h2>
          <p
            className="mt-1 text-on-surface-variant font-normal"
            style={{ fontSize: 14, lineHeight: '20px' }}
            data-testid="cancel-cr-modal-desc"
          >
            {L.CANCEL_CR_MODAL_DESC}
          </p>
        </div>

        <div className="px-6 py-4 flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto">
          {/* Client details — read-only, disabled during submission */}
          <div
            className={`border border-[#DCDDE0] rounded-lg p-4 flex flex-col gap-4 transition-opacity ${
              isSubmitting ? 'opacity-[0.38]' : 'opacity-100'
            }`}
            data-testid="cancel-cr-client-section"
          >
            <p
              className="font-normal text-on-surface-variant"
              style={{ fontSize: 18, lineHeight: '20px' }}
            >
              {L.CANCEL_CR_SECTION_CLIENT_DATA}
            </p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div className="flex flex-col gap-1">
                <p
                  className="font-normal text-on-surface-variant"
                  style={{
                    fontSize: 12,
                    lineHeight: '16px',
                    letterSpacing: '0.4px',
                  }}
                >
                  {L.CANCEL_CR_FIELD_CLIENT_NAME}
                </p>
                <p
                  className="font-normal text-neutral-10 wrap-break-word"
                  style={{ fontSize: 16, lineHeight: '24px' }}
                  data-testid="cancel-cr-client-name"
                >
                  {clientName ?? PLACEHOLDER}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p
                  className="font-normal text-on-surface-variant"
                  style={{
                    fontSize: 12,
                    lineHeight: '16px',
                    letterSpacing: '0.4px',
                  }}
                >
                  {L.CANCEL_CR_FIELD_CLIENT_EMAIL}
                </p>
                <p
                  className="font-normal text-neutral-10 break-all"
                  style={{ fontSize: 16, lineHeight: '24px' }}
                  data-testid="cancel-cr-client-email"
                >
                  {clientEmail ?? PLACEHOLDER}
                </p>
              </div>
            </div>
          </div>

          {/* Cancellation reason — mandatory */}
          <div data-field="cancel-cr-reason" ref={reasonRef}>
            <Textarea
              label={L.CANCEL_CR_REASON_LABEL}
              placeholder={L.CANCEL_CR_REASON_PLACEHOLDER}
              value={cancelNote}
              onChange={(e) =>
                onCancelNoteChange(
                  (e.target as HTMLTextAreaElement).value ?? ''
                )
              }
              maxLength={REASON_MAX_LENGTH}
              rows={4}
              fullWidth
              error={Boolean(cancelNoteError)}
              errorMessage={cancelNoteError}
              disabled={isSubmitting}
              dataTestId="cancel-cr-reason-input"
            />
          </div>

          {submitError && (
            <p
              className="text-error-40"
              style={{ fontSize: 12, lineHeight: '16px' }}
              data-testid="cancel-cr-submit-error"
            >
              {submitError}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 shrink-0">
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg border border-[#DCDDE0] bg-white text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-[0.38] cursor-not-allowed'
                : 'hover:bg-[#F3F4F6] cursor-pointer'
            }`}
            data-testid="cancel-cr-back-btn"
          >
            {L.CANCEL_CR_BUTTON_BACK}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-[0.38] cursor-not-allowed'
                : 'hover:bg-[#1f3340] cursor-pointer'
            }`}
            data-testid="cancel-cr-submit-btn"
          >
            {isSubmitting
              ? L.CANCEL_CR_BUTTON_SUBMITTING
              : L.CANCEL_CR_BUTTON_SUBMIT}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CancelContactRequestModal;
